import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard, StatCardRow } from '@/components/ui/StatCard';
import { formatCount } from '@/lib/numbers';

import { percentChange } from './change';
import { CurrentActivityCard } from './CurrentActivityCard';
import { CARD_METRICS, METRIC_TABLES } from './dashboardData';
import { DateRangeSelect } from './DateRangeSelect';
import { parseRange, toSearchParams, todayIso, type ResolvedRange } from './dateRange';
import { EngagementTrendCard } from './EngagementTrendCard';
import { MetricTable } from './MetricTable';
import { SampleDataNotice } from './SampleDataNotice';
import { DASHBOARD_SOURCE, useDashboard } from './useDashboard';

/** Req §19, and the wireframe: every number here is an interaction, not an outcome. */
export const FOOTNOTE =
  'Interaction metrics reflect activity measurable inside ActivistARC. A button click does not confirm that a call, email, volunteer action, or donation was completed.';

const PDF_LATER = 'Arrives in a later release';

/** Two columns side by side at desktop width, one above the other below it. */
const twoUp = (columns: string) =>
  ({
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: columns },
    mb: 3,
  }) as const;

/**
 * The organization's front page: how people engage with it inside
 * ActivistARC over a chosen range.
 *
 * The range lives in the address, so a reload and a shared link keep it. The
 * header, the range control, the sample notice and the footnote are there in
 * every state; what the numbers fill in holds its size while they load, so
 * nothing moves when they arrive.
 */
export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIso();
  const range = parseRange(searchParams, today);
  const query = useDashboard(range);
  const pdfNoteId = useId();

  const data = query.data;
  const loading = query.isPending;
  const failed = query.isError;
  const retry = () => void query.refetch();

  function changeRange(next: ResolvedRange) {
    // Replaced rather than pushed: a custom range is typed a day at a time,
    // and the back button should not step through every half-typed pair.
    setSearchParams(toSearchParams(next), { replace: true });
  }

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        description="Organization activity and performance at a glance"
        action={
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <DateRangeSelect value={range} onChange={changeRange} today={today} />
            {/* A disabled button fires no pointer events, so the tooltip sits
                on a wrapper. The note is also tied to the button, so a
                screen reader that finds it hears why it does nothing. */}
            <Tooltip title={PDF_LATER} describeChild>
              <span>
                <Button variant="secondary" disabled aria-describedby={pdfNoteId}>
                  Download PDF
                </Button>
              </span>
            </Tooltip>
            <Box component="span" id={pdfNoteId} hidden>
              {PDF_LATER}
            </Box>
          </Box>
        }
      />

      <SampleDataNotice source={data?.source ?? DASHBOARD_SOURCE} />

      {failed ? (
        // TODO(portal-theme-and-states): swap for the shared ErrorState, titled
        // "The dashboard could not be loaded", when Day 13's change lands.
        <Box sx={{ mb: 3 }}>
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            The dashboard could not be loaded.
          </Alert>
          <Button onClick={retry}>Try again</Button>
        </Box>
      ) : (
        <>
          <StatCardRow label="Summary" columns={3}>
            {CARD_METRICS.map((metric) => {
              const numbers = data?.metrics[metric.id];
              return (
                <StatCard
                  key={metric.id}
                  label={metric.card.label}
                  caption={metric.card.caption}
                  value={numbers ? formatCount(numbers.current) : ''}
                  change={
                    numbers
                      ? percentChange(numbers.current, numbers.previous)
                      : { percent: null, direction: 'flat' }
                  }
                  loading={loading}
                />
              );
            })}
          </StatCardRow>

          <Box sx={twoUp('minmax(0, 2fr) minmax(0, 1fr)')}>
            <EngagementTrendCard series={data?.series} />
            <CurrentActivityCard activity={data?.activity} />
          </Box>
        </>
      )}

      <Box sx={twoUp('repeat(2, minmax(0, 1fr))')}>
        {METRIC_TABLES.map((table) => (
          <MetricTable
            key={table.id}
            table={table.id}
            caption={table.caption}
            metrics={data?.metrics}
            loading={loading}
            error={failed ? 'These numbers could not be loaded.' : null}
            onRetry={retry}
          />
        ))}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
        {FOOTNOTE}
      </Typography>
    </Box>
  );
}
